/**
 * The venue picker (ticket #59): Leaflet over OpenStreetMap raster tiles — no API key, no
 * geocoder account. One pin, moved four ways: a click on the map, a drag of the pin, the
 * browser's own location (`Ambil Lokasi Saya` in the caller), or a Nominatim search result.
 *
 * Read-only callers pass `value` and no `onChange`: the map is then a plain pin preview, so
 * they should render nothing at all when the booking has no coordinates.
 */
import { useEffect, useRef, useState } from "react";
import L from "leaflet";

import "leaflet/dist/leaflet.css";

export type Coords = { lat: number; lng: number };

type Suggestion = { label: string; lat: number; lng: number };

/** Jakarta, so the map opens on the country before the first pin lands. */
const DEFAULT_CENTER: [number, number] = [-6.1751, 106.8272];

/** A CSS pin, so no marker image has to survive the bundler. */
const PIN = L.divIcon({ className: "location-pin", html: "<span></span>", iconSize: [22, 22], iconAnchor: [11, 22] });

export function LocationMap({
  value,
  onChange,
  height = 210,
}: Readonly<{ value: Coords | null; onChange?: (coords: Coords) => void; height?: number }>) {
  const box = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);
  const pin = useRef<L.Marker | null>(null);
  // The map is built once; every handler reads the current prop through this ref.
  const change = useRef(onChange);
  change.current = onChange;
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  useEffect(() => {
    const holder = box.current;
    if (!holder || map.current) return;
    const instance = L.map(holder, {
      center: [value?.lat ?? DEFAULT_CENTER[0], value?.lng ?? DEFAULT_CENTER[1]],
      zoom: value ? 16 : 11,
      scrollWheelZoom: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(instance);
    instance.on("click", (event: L.LeafletMouseEvent) => change.current?.({ lat: event.latlng.lat, lng: event.latlng.lng }));
    map.current = instance;
    // The container often mounts inside an animating modal; Leaflet needs one size re-read.
    const settled = setTimeout(() => instance.invalidateSize(), 0);
    return () => {
      clearTimeout(settled);
      instance.remove();
      map.current = null;
      pin.current = null;
    };
  }, []);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    if (!value) {
      pin.current?.remove();
      pin.current = null;
      return;
    }
    if (pin.current) pin.current.setLatLng([value.lat, value.lng]);
    else {
      const marker = L.marker([value.lat, value.lng], { icon: PIN, draggable: Boolean(change.current) }).addTo(instance);
      marker.on("dragend", () => {
        const at = marker.getLatLng();
        change.current?.({ lat: at.lat, lng: at.lng });
      });
      pin.current = marker;
    }
    instance.setView([value.lat, value.lng], Math.max(instance.getZoom(), 15));
  }, [value?.lat, value?.lng]);

  // Nominatim takes a query and answers places; one request per pause in typing, and the
  // app's own country is the only one that matters here.
  useEffect(() => {
    const asked = query.trim();
    if (asked.length < 3) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&countrycodes=id&accept-language=id&q=${encodeURIComponent(asked)}`,
        );
        const rows = (await response.json()) as Array<{ display_name: string; lat: string; lon: string }>;
        setSuggestions(rows.map((row) => ({ label: row.display_name, lat: Number(row.lat), lng: Number(row.lon) })));
      } catch {
        setSuggestions([]);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [query]);

  const pick = (suggestion: Suggestion) => {
    setQuery(suggestion.label);
    setSuggestions([]);
    change.current?.({ lat: suggestion.lat, lng: suggestion.lng });
  };

  return (
    <div className="location-picker">
      {onChange ? (
        <div className="location-search">
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cari alamat / nama tempat…" />
          {suggestions.length ? (
            <ul className="location-suggestions">
              {suggestions.map((suggestion) => (
                <li key={`${suggestion.lat}-${suggestion.lng}`}>
                  <button type="button" onClick={() => pick(suggestion)}>
                    {suggestion.label}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <div className="location-map" ref={box} style={{ height }} />
      {onChange ? <small className="location-note">{value ? "Geser pin untuk memperbaiki titik." : "Klik peta untuk menandai venue."}</small> : null}
    </div>
  );
}
